import axios from "axios";
import { useEffect, useState } from "react";
import { ClipboardCheck, Lock, CheckCircle2, AlertTriangle, CreditCard, Milestone } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { API } from "./shared";

// ============================================================
// VIEW: PRODUCTION MILESTONES (Suppliers)
// Suppliers pick their next stage from an 11-step production timeline
// and the buyer sees the update instantly on their job drawer.
// ============================================================
export function SupplierMilestones({ supplierId, jobs }) {
  const [refKey, setRefKey] = useState(0);
  // Awarded jobs where this supplier is the winner + not yet delivered
  const myJobs = (jobs || []).filter(j =>
    j.awarded_supplier_id === supplierId &&
    !["completed", "canceled"].includes(j.status || "")
  );

  return (
    <section className="content" data-testid="supplier-milestones">
      <div className="section-heading">
        <div>
          <p className="eyebrow">MY ORDERS · PRODUCTION MILESTONES</p>
          <h2>Update the buyer on your progress</h2>
          <p className="subhead">Post a milestone every time you cross a production stage — from prepress verification through dispatch. Buyers rely on these to plan launches and to release escrow on delivery.</p>
        </div>
      </div>

      {myJobs.length === 0 ? (
        <div className="empty-state" data-testid="ms-empty">
          <ClipboardCheck size={22}/>
          <b>No awarded jobs in production</b>
          <span>Once a buyer awards you a job, it will appear here with the milestone timeline.</span>
        </div>
      ) : (
        <div className="ms-grid" data-testid="ms-list">
          {myJobs.map(j => (
            <SupplierMilestoneCard key={`${j.id}-${refKey}`} job={j} onUpdated={() => setRefKey(k => k + 1)} />
          ))}
        </div>
      )}
    </section>
  );
}


export function SupplierMilestoneCard({ job, onUpdated }) {
  const [timeline, setTimeline] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/jobs/${job.id}/milestones`);
      setTimeline(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load milestones.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [job.id]);

  const post = async (code) => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/jobs/${job.id}/milestones`, { code, note: note.trim() || null });
      setNote("");
      await load();
      onUpdated?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update milestone.");
    } finally { setBusy(false); }
  };

  if (!timeline) return <div className="ms-card ms-card-loading" data-testid={`ms-card-${job.id}`}>Loading {job.title}…</div>;

  const nextStage = timeline.stages.find(s => s.state === "current");
  return (
    <article className="ms-card" data-testid={`ms-card-${job.id}`}>
      <header>
        <div>
          <p className="eyebrow small">{job.id}</p>
          <b>{job.title || job.id}</b>
          <span className="ms-status">{job.status} · {job.payment_status || "Awaiting escrow"}</span>
        </div>
        <div className="ms-progress" data-testid={`ms-progress-${job.id}`}>
          <span className="ms-progress-pct">{timeline.percent}%</span>
          <div className="ms-progress-bar"><div style={{ width: `${timeline.percent}%` }}/></div>
          <span className="ms-progress-count">{timeline.completed_count} / {timeline.total} stages</span>
        </div>
      </header>

      <ol className="ms-timeline" data-testid={`ms-timeline-${job.id}`}>
        {timeline.stages.map((s, i) => (
          <li key={s.code} className={`ms-step ms-${s.state}`} data-testid={`ms-step-${job.id}-${s.code}`}>
            <span className="ms-marker">
              {s.state === "done" ? <CheckCircle2 size={16}/> : (s.state === "current" ? <Milestone size={16}/> : <span className="ms-dot"/>)}
            </span>
            <div className="ms-step-body">
              <b>{i + 1}. {s.label}</b>
              {s.state === "done" && s.updated_at && (
                <span className="ms-meta">Marked by {s.updated_by || "supplier"} · {new Date(s.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
              )}
              {s.state === "done" && s.note && <span className="ms-note">"{s.note}"</span>}
              {s.state === "current" && <span className="ms-meta ms-next">Next up — post an update to advance</span>}
            </div>
          </li>
        ))}
      </ol>

      {nextStage ? (
        <div className="ms-actions" data-testid={`ms-actions-${job.id}`}>
          <input
            data-testid={`ms-note-${job.id}`}
            className="ms-note-input"
            placeholder={`Optional note for "${nextStage.label}"`}
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={busy}
          />
          <button
            data-testid={`ms-submit-${job.id}`}
            className="primary-btn"
            disabled={busy}
            onClick={() => post(nextStage.code)}
          >
            {busy ? "Updating…" : `Mark "${nextStage.label}" complete →`}
          </button>
        </div>
      ) : (
        <div className="ms-complete" data-testid={`ms-complete-${job.id}`}>
          <CheckCircle2 size={16}/> All 11 milestones complete — buyer has been notified.
        </div>
      )}

      {err && <div className="form-error" data-testid={`ms-error-${job.id}`}>{err}</div>}
    </article>
  );
}


// ============================================================
// VIEW: RAZORPAY PAYOUTS (Suppliers / Agencies)
// ============================================================
export const RZP_STATUS_LABEL = {
  requested: { label: "Submitted — awaiting Razorpay review", tone: "pending" },
  under_review: { label: "Under review by Razorpay", tone: "pending" },
  needs_clarification: { label: "Needs clarification — update required", tone: "pending" },
  activated: { label: "Payouts active", tone: "ok" },
  suspended: { label: "Suspended by Razorpay", tone: "pending" },
};

// Decides between Manual payout profile (default) or full Razorpay Route KYC
// based on the platform's PAYOUT_MODE flag exposed by /api/razorpay/config.
export function PayoutsRouter({ supplierId }) {
  const [mode, setMode] = useState(null);
  useEffect(() => {
    axios.get(`${API}/razorpay/config`)
      .then(r => setMode(r.data?.payout_mode || "manual"))
      .catch(() => setMode("manual"));
  }, []);
  if (!mode) return <div className="content" data-testid="payouts-loading">Loading payouts…</div>;
  if (mode === "razorpay_route") return <RazorpayOnboarding supplierId={supplierId}/>;
  return <ManualPayoutProfile supplierId={supplierId}/>;
}


export function ManualPayoutProfile({ supplierId }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    beneficiary_name: "", pan: "", gstin: "",
    account_number: "", ifsc_code: "",
    bank_name: "", account_type: "savings",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const load = async () => {
    if (!supplierId) return;
    try {
      const { data } = await axios.get(`${API}/users/${supplierId}/payout-profile`);
      setProfile(data);
      if (data?.beneficiary_name) {
        setForm({
          beneficiary_name: data.beneficiary_name || "",
          pan: data.pan || "",
          gstin: data.gstin || "",
          account_number: data.account_number || "",
          ifsc_code: data.ifsc_code || "",
          bank_name: data.bank_name || "",
          account_type: data.account_type || "savings",
        });
      }
    } catch { setProfile({}); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [supplierId]);

  const submit = async () => {
    setBusy(true); setError(""); setSaved(false);
    try {
      await axios.put(`${API}/users/${supplierId}/payout-profile`, {
        ...form,
        pan: form.pan.toUpperCase().trim(),
        gstin: form.gstin.toUpperCase().trim() || null,
        ifsc_code: form.ifsc_code.toUpperCase().trim(),
      });
      setSaved(true);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save payout profile.");
    } finally { setBusy(false); }
  };

  const isSaved = !!profile?.beneficiary_name;

  return (
    <section className="content" data-testid="manual-payouts">
      <div className="section-heading">
        <div>
          <p className="eyebrow">BANK DETAILS · WEEKLY PAYOUTS</p>
          <h2>Get paid directly to your bank</h2>
          <p className="subhead">GoPrintPack will transfer every completed job's payout (minus the applicable platform take-rate and taxes) to this account within 48 hours of delivery confirmation. Share this once — we'll reuse it for every order.</p>
        </div>
      </div>

      <div className="stripe-panel" data-testid="manual-payout-panel">
        <div className={`stripe-status ${isSaved ? "ok" : "pending"}`}>
          {isSaved ? <CheckCircle2 size={19}/> : <AlertTriangle size={19}/>}
          <div>
            <b data-testid="manual-payout-status">{isSaved ? "Bank details saved — you're ready for payouts" : "Add your bank details to receive payouts"}</b>
            <span>{isSaved
              ? `${profile.beneficiary_name} · ${profile.bank_name || "Bank"} ••${profile.account_last4} · IFSC ${profile.ifsc_code}`
              : "Payouts cannot be issued without a verified bank account."}</span>
          </div>
        </div>

        <div className="stripe-checklist">
          <ChecklistRow done={!!form.beneficiary_name} label="Beneficiary name (as per bank)"/>
          <ChecklistRow done={!!form.pan} label="PAN submitted (required for TDS)"/>
          <ChecklistRow done={!!form.account_number || !!profile?.account_last4} label="Bank account & IFSC"/>
          <ChecklistRow done={isSaved} label="Profile saved — eligible for next payout cycle"/>
        </div>

        <div className="stripe-form" style={{display:"grid",gap:14,marginTop:16}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <RzpField label="Beneficiary name (exactly as on bank statement)" testid="mp-benef" value={form.beneficiary_name} onChange={v => setForm({...form, beneficiary_name:v})} placeholder="Mosaic Press LLP"/>
            <RzpField label="PAN (business or individual)" testid="mp-pan" value={form.pan} onChange={v => setForm({...form, pan:v})} placeholder="AAACL1234C" maxLength={10}/>
            <RzpField label="GSTIN (optional — needed if you want to invoice us with GST)" testid="mp-gstin" value={form.gstin} onChange={v => setForm({...form, gstin:v})} placeholder="18AABCU9603R1ZM" maxLength={15}/>
            <RzpField label="Bank name (optional)" testid="mp-bank" value={form.bank_name} onChange={v => setForm({...form, bank_name:v})} placeholder="HDFC Bank"/>
            <RzpField label="Account number" testid="mp-acc" value={form.account_number} onChange={v => setForm({...form, account_number:v})} placeholder={isSaved && !form.account_number ? `••${profile.account_last4}` : "e.g. 12345678901234"}/>
            <RzpField label="IFSC code" testid="mp-ifsc" value={form.ifsc_code} onChange={v => setForm({...form, ifsc_code:v})} placeholder="HDFC0000317" maxLength={11}/>
            <label style={{fontSize:12,color:"#6b7280"}}>Account type
              <select data-testid="mp-acc-type" value={form.account_type} onChange={e => setForm({...form, account_type:e.target.value})} style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4}}>
                <option value="savings">Savings</option>
                <option value="current">Current</option>
              </select>
            </label>
          </div>

          <button data-testid="manual-payout-save" className="primary-btn full" disabled={busy} onClick={submit}>
            {busy ? "Saving…" : (isSaved ? "Update bank details" : "Save bank details")} <CreditCard size={16}/>
          </button>
        </div>

        {saved && <p data-testid="manual-payout-saved" style={{color:"#047857",fontSize:13,marginTop:10,display:"flex",alignItems:"center",gap:6}}><CheckCircle2 size={14}/> Saved. Next payout will be wired to this account.</p>}
        {error && <p className="stripe-mock-note" data-testid="manual-payout-error" style={{color:"#B91C1C"}}><Lock size={13}/> {error}</p>}

        <div style={{background:"#F0F9FF",border:"1px solid #BAE6FD",padding:12,borderRadius:8,marginTop:14,fontSize:13,color:"#075985"}}>
          <b>How payouts work right now:</b>
          <ol style={{marginTop:6,paddingLeft:20,lineHeight:1.7}}>
            <li>Buyer confirms delivery of your job on GoPrintPack</li>
            <li>Our team wires the net payout (gross − applicable commission, taxes and TDS) to the bank above within 48 hours</li>
            <li>You receive an SMS/email with the UTR reference once sent</li>
          </ol>
        </div>
      </div>
    </section>
  );
}



export function RazorpayOnboarding({ supplierId }) {
  const { user } = useAuth();
  const [status, setStatus] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    business_name: "", contact_name: "", phone: "", business_type: "individual",
    pan: "", gstin: "",
    street1: "", street2: "", city: "", state: "", postal_code: "",
    stakeholder_name: "", stakeholder_email: "", stakeholder_pan: "",
    beneficiary_name: "", account_number: "", ifsc_code: "",
  });

  const loadStatus = async () => {
    if (!supplierId) return;
    try {
      const [me, st] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/users/${supplierId}/razorpay/status`),
      ]);
      setStatus({
        loading: false,
        onboarded: st.data.onboarded,
        account_id: st.data.account_id,
        activation_status: st.data.activation_status,
        requirements: st.data.requirements || [],
        settlement_last4: st.data.settlement_last4,
        kyc_pan: st.data.kyc_pan,
        kyc_gstin: st.data.kyc_gstin,
      });
      setForm(f => ({
        ...f,
        business_name: f.business_name || me.data.name || "",
        contact_name: f.contact_name || me.data.name || "",
        stakeholder_name: f.stakeholder_name || me.data.name || "",
        stakeholder_email: f.stakeholder_email || me.data.email || "",
        phone: f.phone || me.data.phone || "",
      }));
    } catch {
      setStatus({ loading: false, onboarded: false });
    }
  };
  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [supplierId]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = {
        ...form,
        pan: form.pan.toUpperCase().trim(),
        gstin: form.gstin.toUpperCase().trim() || null,
        stakeholder_pan: form.stakeholder_pan.toUpperCase().trim(),
        ifsc_code: form.ifsc_code.toUpperCase().trim(),
      };
      await axios.post(`${API}/users/${supplierId}/razorpay/onboard`, payload);
      await loadStatus();
    } catch (e) {
      setError(e?.response?.data?.detail || "Onboarding failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const refreshStatus = async () => {
    setBusy(true);
    try { await loadStatus(); } finally { setBusy(false); }
  };

  if (status.loading) {
    return <div className="content" data-testid="razorpay-loading">Loading payout status…</div>;
  }

  const activation = status.activation_status;
  const tone = activation && RZP_STATUS_LABEL[activation]?.tone || (status.onboarded ? "pending" : "pending");
  const statusCopy = activation
    ? RZP_STATUS_LABEL[activation]?.label || `Status: ${activation}`
    : (status.onboarded ? "Linked account created — complete KYC below" : "Onboarding required");

  return (
    <section className="content" data-testid="razorpay-payouts">
      <div className="section-heading">
        <div>
          <p className="eyebrow">RAZORPAY ROUTE · SUPPLIER PAYOUTS</p>
          <h2>Get paid on delivery</h2>
          <p className="subhead">Connect a Razorpay linked account to receive escrow payouts within 48 hours of delivery confirmation, minus the commission on your plan (8% Starter · 6% Pro · 4% Enterprise for printers and partners).</p>
        </div>
      </div>
      <div className="stripe-panel" data-testid="razorpay-panel">
        <div className={`stripe-status ${tone}`}>
          {tone === "ok" ? <CheckCircle2 size={19}/> : <AlertTriangle size={19}/>}
          <div>
            <b>{statusCopy}</b>
            <span data-testid="razorpay-account-id">
              {status.account_id ? `Linked account · ${status.account_id}` : "Submit the form below to begin"}
              {status.settlement_last4 ? ` · Bank ••${status.settlement_last4}` : ""}
            </span>
          </div>
          {status.account_id && (
            <button data-testid="rzp-refresh-status" className="secondary-btn" disabled={busy} onClick={refreshStatus} style={{marginLeft:"auto"}}>
              Refresh status
            </button>
          )}
        </div>

        {status.requirements && status.requirements.length > 0 && (
          <div className="form-error" data-testid="rzp-requirements" style={{background:"#FEF3C7",border:"1px solid #FCD34D",padding:12,borderRadius:8,margin:"12px 0"}}>
            <b>Razorpay needs these fields:</b>
            <ul style={{marginTop:6,paddingLeft:18}}>
              {status.requirements.map((r, i) => (
                <li key={i}><code>{r.field_reference || r.reference_id || JSON.stringify(r)}</code>{r.description ? ` — ${r.description}` : ""}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="stripe-checklist">
          <ChecklistRow done={!!status.account_id} label="Linked account created"/>
          <ChecklistRow done={!!status.kyc_pan} label="PAN / GST submitted"/>
          <ChecklistRow done={!!status.settlement_last4} label="Bank account connected"/>
          <ChecklistRow done={activation === "activated"} label="Razorpay activated for payouts"/>
        </div>

        {activation !== "activated" && (
          <div className="stripe-form" style={{display:"grid",gap:14,marginTop:16}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <RzpField label="Business name" testid="rzp-business-name" value={form.business_name} onChange={v => setForm({...form, business_name:v})} placeholder="e.g. Mosaic Press LLP"/>
              <RzpField label="Contact name" testid="rzp-contact-name" value={form.contact_name} onChange={v => setForm({...form, contact_name:v})} placeholder="Owner / director"/>
              <RzpField label="Phone (10-digit)" testid="rzp-phone" value={form.phone} onChange={v => setForm({...form, phone:v})} placeholder="9000090000"/>
              <label style={{fontSize:12,color:"#6b7280"}}>Business type
                <select data-testid="rzp-business-type" value={form.business_type} onChange={e => setForm({...form, business_type:e.target.value})} style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4}}>
                  <option value="individual">Individual / Sole proprietor</option>
                  <option value="proprietorship">Proprietorship</option>
                  <option value="partnership">Partnership</option>
                  <option value="llp">LLP</option>
                  <option value="private_limited">Private Limited</option>
                  <option value="public_limited">Public Limited</option>
                </select>
              </label>
              <RzpField label="Business PAN" testid="rzp-pan" value={form.pan} onChange={v => setForm({...form, pan:v})} placeholder="AAACL1234C" maxLength={10}/>
              <RzpField label="GSTIN (optional)" testid="rzp-gstin" value={form.gstin} onChange={v => setForm({...form, gstin:v})} placeholder="18AABCU9603R1ZM" maxLength={15}/>
            </div>

            <div><b style={{fontSize:13,color:"#111827"}}>Registered address</b></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <RzpField label="Street line 1" testid="rzp-street1" value={form.street1} onChange={v => setForm({...form, street1:v})} placeholder="Survey No-312/1, Village-Kokamthan" minLen={10}/>
              <RzpField label="Street line 2 (optional)" testid="rzp-street2" value={form.street2} onChange={v => setForm({...form, street2:v})} placeholder="MG Road"/>
              <RzpField label="City" testid="rzp-city" value={form.city} onChange={v => setForm({...form, city:v})} placeholder="Kopargaon"/>
              <RzpField label="State" testid="rzp-state" value={form.state} onChange={v => setForm({...form, state:v})} placeholder="KARNATAKA"/>
              <RzpField label="Postal code" testid="rzp-postal" value={form.postal_code} onChange={v => setForm({...form, postal_code:v})} placeholder="560034" maxLength={6}/>
            </div>

            <div><b style={{fontSize:13,color:"#111827"}}>Director / stakeholder (one allowed by Razorpay Route)</b></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <RzpField label="Stakeholder name" testid="rzp-sh-name" value={form.stakeholder_name} onChange={v => setForm({...form, stakeholder_name:v})} placeholder="Director's full name"/>
              <RzpField label="Stakeholder email" testid="rzp-sh-email" value={form.stakeholder_email} onChange={v => setForm({...form, stakeholder_email:v})} placeholder="director@company.com"/>
              <RzpField label="Stakeholder PAN (4th char = 'P')" testid="rzp-sh-pan" value={form.stakeholder_pan} onChange={v => setForm({...form, stakeholder_pan:v})} placeholder="AVOPB1111K" maxLength={10}/>
            </div>

            <div><b style={{fontSize:13,color:"#111827"}}>Settlement bank account</b></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <RzpField label="Beneficiary name (as per bank)" testid="rzp-benef" value={form.beneficiary_name} onChange={v => setForm({...form, beneficiary_name:v})} placeholder="Account holder's exact name"/>
              <RzpField label="Account number" testid="rzp-acc" value={form.account_number} onChange={v => setForm({...form, account_number:v})} placeholder="12345678901234"/>
              <RzpField label="IFSC code" testid="rzp-ifsc" value={form.ifsc_code} onChange={v => setForm({...form, ifsc_code:v})} placeholder="HDFC0000317" maxLength={11}/>
            </div>

            <button data-testid="razorpay-onboard-button" className="primary-btn full" disabled={busy} onClick={submit}>
              {busy ? "Submitting…" : (status.account_id ? "Update KYC & bank details" : "Submit for Razorpay activation")} <CreditCard size={16}/>
            </button>
          </div>
        )}

        {error && <p className="stripe-mock-note" data-testid="razorpay-onboard-msg" style={{color:"#B91C1C"}}><Lock size={13}/> {error}</p>}
      </div>
    </section>
  );
}

export const RzpField = ({ label, testid, value, onChange, placeholder, maxLength, minLen }) => (
  <label style={{fontSize:12,color:"#6b7280"}}>{label}
    <input
      data-testid={testid}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      minLength={minLen}
      style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4}}
    />
  </label>
);

export const ChecklistRow = ({ done, label }) => (
  <div className={`checklist-row ${done ? "done" : ""}`}>
    <span className="check">{done ? "✓" : ""}</span>
    <span>{label}</span>
  </div>
);
